<template>
  <!--
    WHAT: Structured editor for a metric's opportunity_config — the "Opportunity
          Sync" block that generates/expires worklist opportunities from the
          metric: enable toggle, source opportunity primitive, entity resolution,
          create/expire/update/unexpire strategies, dedupe primary keys,
          assignment, flag-weighted + due-date priority, and an Advanced raw-JSON
          escape hatch for long-tail keys.
    WHERE: [reused verbatim from v1] Wrapped by OpportunitySection.vue (the metric
           "Opportunity" detail tab). It draws its own card (selfChrome).
    CONTRACT:
      props:
        modelValue (Object)   — opportunity_config
        saving (Boolean)      — show "Saving…" on the Save button
        savedFlash (Boolean)  — show the transient "Saved" note
        primitives (Array)    — [{workflow_id,name,role}] opportunity source options (wrapper workflows)
        patientFlags (Array)  — [{id,name,label,color,type,values}] for priority
      emits: update:modelValue, save, revert
    NOTES: [reused verbatim from v1] shared component — do NOT restyle without
           checking other callers. Gotchas: `enabled` == config has any keys, so
           disabling clears the whole object; the `priority` block holds two
           INDEPENDENT terms (flag-weighted + due-date) each gated by its own
           toggle, and is omitted entirely when both are off (legacy metrics are
           never touched); categorical flags carry their own per-value weights so
           no per-metric weight is written for them; Save is blocked while the
           Advanced JSON is invalid. ADVANCED_KEYS are cleared and re-applied from
           the textarea on every save, so the textarea is their source of truth —
           a value written directly to the DB is lost if someone saves this panel
           from a stale load. on_create_dossiers / superseded_by / supersedes are
           STRUCTURED (own widgets) and not exposed to that hazard.
  -->
  <div class="bg-white rounded-lg border border-gray-200">
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
      <div class="flex items-center gap-2">
        <span class="text-sm font-semibold text-gray-800">Opportunity Sync</span>
        <span
          v-if="enabled"
          class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
        >active</span>
        <span v-else class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-50 text-gray-400 border border-gray-200">off</span>
      </div>
      <div class="flex items-center gap-2.5">
        <span v-if="saveError" class="text-[10px] font-medium text-red-600">Save failed</span>
        <span v-else-if="dirty" class="text-[10px] font-medium text-amber-600">Unsaved</span>
        <span v-else-if="savedFlash" class="text-[10px] font-medium text-emerald-600">Saved</span>
        <button
          v-if="dirty"
          class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          @click="revert"
        >Revert</button>
        <button
          class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          :disabled="!dirty || saving"
          @click="save"
        >{{ saving ? 'Saving…' : 'Save' }}</button>
      </div>
    </div>

    <div class="p-4 space-y-4">
      <!-- Enable toggle -->
      <label class="flex items-center gap-2 text-xs text-gray-700">
        <input type="checkbox" class="rounded" :checked="enabled" @change="toggleEnabled($event.target.checked)" />
        <span class="font-medium">Generate opportunities from this metric</span>
      </label>

      <template v-if="enabled">
        <!-- Source primitive -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Opportunity primitive</label>
            <select
              v-model="sourceWorkflowId"
              class="text-xs px-2 py-1.5 border border-gray-200 rounded w-full text-gray-800 focus:outline-none focus:border-blue-400"
              @change="onSourceWorkflow"
            >
              <option :value="null">— select a primitive —</option>
              <option v-for="p in primitives" :key="p.workflow_id" :value="p.workflow_id">
                {{ p.name }}<template v-if="p.role && p.role !== 'opportunity'"> ({{ p.role }})</template>
              </option>
              <option
                v-if="cfg.opportunity_workflow_id && !primitives.some(p => p.workflow_id === cfg.opportunity_workflow_id)"
                :value="cfg.opportunity_workflow_id"
              >Workflow #{{ cfg.opportunity_workflow_id }} (current)</option>
            </select>
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Entity resolution</label>
            <select v-model="cfg.entity_resolution" class="text-xs px-2 py-1.5 border border-gray-200 rounded w-full text-gray-800 focus:outline-none focus:border-blue-400" @change="emit">
              <option :value="undefined">default (provider)</option>
              <option value="patient">patient</option>
            </select>
          </div>
        </div>

        <!-- Strategies -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Create strategy</label>
            <select v-model="cfg.create_strategy" class="text-xs px-2 py-1.5 border border-gray-200 rounded w-full text-gray-800 focus:outline-none focus:border-blue-400" @change="emit">
              <option value="create">create</option>
              <option value="none">none</option>
              <option value="merge_cm_programs">merge_cm_programs</option>
            </select>
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Expire strategy</label>
            <select v-model="cfg.expire_strategy" class="text-xs px-2 py-1.5 border border-gray-200 rounded w-full text-gray-800 focus:outline-none focus:border-blue-400" @change="emit">
              <option value="none">none</option>
              <option value="expire_missing">expire_missing</option>
              <option value="expire_valid_thru">expire_valid_thru</option>
              <option value="expire_missing_and_valid_thru">expire_missing_and_valid_thru</option>
            </select>
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Update strategy</label>
            <select v-model="updateMode" class="text-xs px-2 py-1.5 border border-gray-200 rounded w-full text-gray-800 focus:outline-none focus:border-blue-400" @change="onUpdateMode">
              <option value="all">all metadata fields</option>
              <option value="none">none</option>
              <option value="merge">merge</option>
              <option value="fields">specific fields…</option>
            </select>
            <input
              v-if="updateMode === 'fields'"
              v-model="updateFieldsText"
              class="mt-1.5 text-xs px-2 py-1.5 border border-gray-200 rounded w-full font-mono text-gray-800 focus:outline-none focus:border-blue-400"
              placeholder="priority_score, next_appointment_date"
              @input="onUpdateFields"
            />
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Unexpire strategy</label>
            <select v-model="cfg.unexpire_strategy" class="text-xs px-2 py-1.5 border border-gray-200 rounded w-full text-gray-800 focus:outline-none focus:border-blue-400" @change="emit">
              <option value="none">none</option>
              <option value="reactivate">reactivate</option>
            </select>
          </div>
        </div>

        <!-- Primary keys -->
        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Opportunity primary keys (dedupe grain)</label>
          <div class="space-y-1">
            <div v-for="(k, i) in primaryKeys" :key="i" class="flex items-center gap-1.5">
              <input
                v-model="primaryKeys[i]"
                class="text-xs px-2 py-1.5 border border-gray-200 rounded flex-1 font-mono text-gray-800 focus:outline-none focus:border-blue-400"
                placeholder="PATIENT_IDENTIFIER or OPPORTUNITY_METADATA.field"
                @input="onPrimaryKeys"
              />
              <button class="text-gray-400 hover:text-red-500 text-sm" @click="removeKey(i)">&times;</button>
            </div>
          </div>
          <button class="mt-1 font-mono text-[11px] text-blue-600 hover:text-blue-700" @click="addKey">＋ add key</button>
        </div>

        <!-- Assignment -->
        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1.5">Assignment</label>
          <div class="flex items-center gap-4">
            <label class="flex items-center gap-1.5 text-xs text-gray-700">
              <input type="checkbox" class="rounded" :checked="assign.auto_assign" @change="setAssign('auto_assign', $event.target.checked)" /> auto-assign
            </label>
            <label class="flex items-center gap-1.5 text-xs text-gray-700">
              <input type="checkbox" class="rounded" :checked="assign.lock_assignment" @change="setAssign('lock_assignment', $event.target.checked)" /> lock assignment
            </label>
            <label class="flex items-center gap-1.5 text-xs text-gray-700 ml-auto">
              <input type="checkbox" class="rounded" :checked="!!cfg.exclude_from_general_sync" @change="setExclude($event.target.checked)" /> exclude from general sync
            </label>
          </div>
        </div>

        <!-- Priority — flag-weighted score, gated by an EXPLICIT toggle.
             priority_score = base + Σ weights of the selected Patient Flags ON
             for the patient. When the toggle is OFF, priority is left entirely
             to the primitive DA (legacy) — a priority block present but toggled
             off does NOTHING. This mirrors the backend: flag scoring runs only
             when priority.use_flag_priority === true. -->
        <div class="border-t border-gray-100 pt-3">
          <div class="flex items-center justify-between mb-2">
            <label class="flex items-center gap-2 text-[11px] font-medium text-gray-600">
              <input
                v-model="useFlagPriority"
                type="checkbox"
                class="rounded"
                @change="onPriority"
              />
              Use flag-weighted priority
            </label>
            <button
              v-if="useFlagPriority"
              class="font-mono text-[11px] text-blue-600 hover:text-blue-700"
              @click="addPriorityFlag"
            >＋ add flag</button>
          </div>
          <p v-if="!useFlagPriority" class="text-[10px] text-gray-400 italic">
            Off — priority left to the primitive DA (legacy). No opportunity is affected.
          </p>
          <template v-else>
          <div class="flex items-center gap-2 mb-2">
            <label class="text-[11px] text-gray-500">Base score</label>
            <input
              v-model.number="priorityBase"
              type="number"
              class="text-xs px-2 py-1 border border-gray-200 rounded w-24 font-mono text-gray-800 focus:outline-none focus:border-blue-400"
              placeholder="0"
              @input="onPriority"
            />
          </div>
          <p class="text-[10px] text-gray-400 mb-2">
            priority_score = base + Σ(weight) for each selected flag that is ON for the patient.
          </p>
          <div v-if="!priorityFlags.length" class="text-[11px] text-gray-400 italic">No flags selected yet.</div>
          <div v-for="(f, i) in priorityFlags" :key="i" class="mb-1.5">
            <div class="flex items-center gap-2">
              <select
                v-model.number="f.flag_id"
                class="text-xs px-2 py-1 border border-gray-200 rounded flex-1 min-w-0 text-gray-800 focus:outline-none focus:border-blue-400"
                @change="onPriority"
              >
                <option :value="null">— select flag —</option>
                <option v-for="fl in patientFlags" :key="fl.id" :value="fl.id">{{ (fl.label || fl.name) }} ({{ fl.id }})</option>
                <option
                  v-if="f.flag_id && !patientFlags.some(fl => fl.id === f.flag_id)"
                  :value="f.flag_id"
                >#{{ f.flag_id }} (current)</option>
              </select>
              <!-- Boolean: one editable weight. Categorical: the weights live on
                   the flag itself (per value), so there is nothing to edit here —
                   only to SHOW, as tier chips below. -->
              <input
                v-if="!isCategorical(f.flag_id)"
                v-model.number="f.weight"
                type="number"
                class="text-xs px-2 py-1 border border-gray-200 rounded w-24 font-mono text-gray-800 focus:outline-none focus:border-blue-400"
                placeholder="weight"
                @input="onPriority"
              />
              <span
                v-else
                class="text-[10px] text-gray-400 flex-shrink-0 w-24 text-right"
                title="Weights are set per tier on the flag itself (Patient Flags → Presentation), and are shared by every metric using this flag."
              >per tier ↓</span>
              <button class="text-gray-400 hover:text-red-500 text-sm flex-shrink-0" @click="removePriorityFlag(i)">&times;</button>
            </div>
            <!-- Tier chips, one per DISTINCT tier — not one per raw value. LACE
                 declares 17 values that collapse to 4 tiers and ECHO 9 to 3, so
                 listing values printed "ECHO Low +5" four times and ran off the
                 row. Grouped by (label, weight), with the raw scores that map to
                 each tier shown as its range.

                 READ-ONLY BY DESIGN, not an oversight. These weights live on the
                 FLAG (api_patientflag.flag_config.values[]), so they are shared by
                 every metric that lists it — Acuity is on all 11 tenant-2 CM
                 metrics. Editing one here would silently re-score ten other
                 programs, so the flag's own editor owns them. -->
            <div v-if="isCategorical(f.flag_id)" class="flex flex-wrap gap-1 mt-1 ml-2 pl-2 border-l-2 border-gray-100">
              <span
                v-for="t in categoricalTiers(f.flag_id)"
                :key="t.label + ':' + t.weight"
                class="inline-flex items-baseline gap-1 text-[10px] px-1.5 py-0.5 rounded border"
                :class="tierChipClass(t.color)"
                :title="`Raw ${t.range} → ${t.label}, +${t.weight} priority. Set on the flag; shared by every metric using it.`"
              >
                <span class="font-medium">{{ t.label }}</span>
                <span class="font-mono opacity-60">{{ t.range }}</span>
                <span class="font-mono font-semibold">+{{ t.weight }}</span>
              </span>
              <span
                v-if="!categoricalTiers(f.flag_id).length"
                class="text-[10px] text-gray-400 italic"
              >No tiers defined on this flag.</span>
            </div>
          </div>
          </template>
        </div>

        <!-- Due-date priority — a config-driven urgency curve on the task due
             date, added to the score at READ time (against today). Independent
             of the flag toggle. Program (med-adherence/care-gap) outcome
             urgency is expressed in the opportunity DAs, not here. -->
        <div class="border-t border-gray-100 pt-3">
          <div class="flex items-center justify-between mb-2">
            <label class="flex items-center gap-2 text-[11px] font-medium text-gray-600">
              <input
                v-model="useDueDatePriority"
                type="checkbox"
                class="rounded"
                @change="onDueDate"
              />
              Add task-due-date urgency to priority
            </label>
          </div>
          <p v-if="!useDueDatePriority" class="text-[10px] text-gray-400 italic">
            Off — no due-date term. Ordering is unaffected by task due dates.
          </p>
          <template v-else>
            <p class="text-[10px] text-gray-400 mb-2">
              points = clamp(weight × (horizon − days-until-due), 0 … max points) for the
              soonest active task — a continuous ramp: sooner due (and longer overdue) ranks
              higher. Added to priority_score at read time.
            </p>
            <div class="flex items-center gap-2 mb-1.5">
              <label class="text-[10px] text-gray-500 w-20">Weight /day</label>
              <input
                v-model.number="dueWeight"
                type="number"
                class="text-xs px-2 py-1 border border-gray-200 rounded w-24 font-mono text-gray-800 focus:outline-none focus:border-blue-400"
                placeholder="e.g. 3"
                @input="onDueDate"
              />
            </div>
            <div class="flex items-center gap-2 mb-1.5">
              <label class="text-[10px] text-gray-500 w-20">Max points</label>
              <input
                v-model.number="dueMaxPoints"
                type="number"
                class="text-xs px-2 py-1 border border-gray-200 rounded w-24 font-mono text-gray-800 focus:outline-none focus:border-blue-400"
                placeholder="cap"
                @input="onDueDate"
              />
            </div>
            <div class="flex items-center gap-2 mb-1.5">
              <label class="text-[10px] text-gray-500 w-20">Horizon (days)</label>
              <input
                v-model.number="dueHorizon"
                type="number"
                class="text-xs px-2 py-1 border border-gray-200 rounded w-24 font-mono text-gray-800 focus:outline-none focus:border-blue-400"
                placeholder="0 = overdue only"
                @input="onDueDate"
              />
            </div>
          </template>
        </div>

        <!-- Supersession — which programs block this one (superseded_by) and
             which this one absorbs (supersedes). Structured because the two are
             different mechanisms with different blast radius, and raw JSON gave
             no clue that `supersedes` is inert unless the create strategy is
             merge_cm_programs. -->
        <div class="border-t border-gray-100 pt-3">
          <SupersessionEditor
            :superseded-by="supersededBy"
            :supersedes="supersedesList"
            :create-strategy="cfg.create_strategy || ''"
            :metrics="metrics"
            :self-slug="selfSlug"
            @update:supersededBy="onSupersededBy"
            @update:supersedes="onSupersedes"
          />
        </div>

        <!-- Dossiers — patient Hermes templates attached to this metric's
             opportunities. Structured rather than raw JSON because `refresh` is a
             product decision (once vs daily), not a one-off engineering act. -->
        <div class="border-t border-gray-100 pt-3">
          <DossierBindingsEditor
            v-model="dossiers"
            :dossier-templates="dossierTemplates"
            @update:modelValue="onDossiers"
          />
        </div>

        <!-- Advanced (raw JSON for the long-tail keys: filters, eligibility_rules,
             allowed_contract_ids) — structured editors not warranted for the
             handful of metrics that use them.

             excluded_contract_ids is INTENTIONALLY ABSENT. It is a DENYLIST, so
             it fails OPEN: anything missing from it is permitted, and a dropped
             element silently resumes opportunity creation for a contract the
             customer asked to switch off. That is not hypothetical — metric 883
             lost one entry and created 69 unwanted DHSMVP opportunities over ten
             days before anyone noticed (2026-08-16). Since these keys are cleared
             and re-applied from this textarea on every save, exposing it here
             makes a stale editor able to delete an entry. Keep it out until it
             has a structured editor that shows the COMPLEMENT (which contracts
             are still permitted) rather than the list itself. -->
        <details class="border-t border-gray-100 pt-3">
          <summary class="text-[11px] font-medium text-gray-500 cursor-pointer select-none">Advanced (filters, eligibility, contracts)</summary>
          <textarea
            v-model="advancedJson"
            rows="8"
            class="mt-2 w-full px-3 py-2 text-xs font-mono border rounded focus:outline-none resize-y"
            :class="advancedError ? 'border-red-400 bg-red-50/30' : 'border-gray-200 focus:border-blue-400'"
            spellcheck="false"
            @input="onAdvanced"
          ></textarea>
          <p v-if="advancedError" class="text-[10px] text-red-500 mt-1">{{ advancedError }}</p>
          <p v-else class="text-[10px] text-gray-400 mt-1">Keys here merge into opportunity_config (filters, eligibility_rules, allowed_contract_ids).</p>
        </details>
      </template>
    </div>
  </div>
</template>

<script>
// Advanced/long-tail keys edited as raw JSON; everything common is a real field.
// (excluded_contract_ids is deliberately NOT here — see the note in the template.)
const ADVANCED_KEYS = ['filters', 'eligibility_rules', 'allowed_contract_ids'];
const STRUCTURED_KEYS = [
  'opportunity_workflow_id', 'entity_resolution', 'create_strategy', 'expire_strategy',
  'update_strategy', 'unexpire_strategy', 'opportunity_primary_keys', 'assignment_config',
  'exclude_from_general_sync', 'priority', 'on_create_dossiers',
  'superseded_by', 'supersedes',
];

import DossierBindingsEditor from '@/components/Admin/MetricStudio/panels/DossierBindingsEditor.vue';
import SupersessionEditor from '@/components/Admin/MetricStudio/panels/SupersessionEditor.vue';

export default {
  name: 'OpportunityConfigPanel',
  components: { DossierBindingsEditor, SupersessionEditor },
  props: {
    modelValue: { type: Object, default: () => ({}) },   // opportunity_config
    saving: { type: Boolean, default: false },
    savedFlash: { type: Boolean, default: false },
    saveError: { type: Boolean, default: false },
    primitives: { type: Array, default: () => [] },      // [{workflow_id,name,role}]
    patientFlags: { type: Array, default: () => [] },    // [{id,name,label,color}]
    dossierTemplates: { type: Array, default: () => [] },     // [{id,name}] patient Hermes templates
    metrics: { type: Array, default: () => [] },         // [{id,name,metric_name_slug}] supersession pickers
    selfSlug: { type: String, default: '' },             // this metric's slug (excluded from pickers)
  },
  emits: ['update:modelValue', 'save', 'revert'],
  data() {
    return {
      cfg: this.clone(this.modelValue),
      sourceWorkflowId: (this.modelValue || {}).opportunity_workflow_id || null,
      primaryKeys: [...((this.modelValue || {}).opportunity_primary_keys || [])],
      priorityBase: (((this.modelValue || {}).priority) || {}).base ?? 0,
      priorityFlags: this.derivePriorityFlags(this.modelValue),
      useFlagPriority: (((this.modelValue || {}).priority) || {}).use_flag_priority === true,
      useDueDatePriority: this.deriveUseDueDate(this.modelValue),
      dueWeight: this.deriveDue(this.modelValue, 'weight', null),
      dueMaxPoints: this.deriveDue(this.modelValue, 'max_points', null),
      dueHorizon: this.deriveDue(this.modelValue, 'horizon', null),
      updateMode: this.deriveUpdateMode(this.modelValue),
      updateFieldsText: this.deriveUpdateFields(this.modelValue),
      dossiers: [...((this.modelValue || {}).on_create_dossiers || [])],
      supersededBy: [...((this.modelValue || {}).superseded_by || [])],
      supersedesList: [...((this.modelValue || {}).supersedes || [])],
      advancedJson: this.deriveAdvanced(this.modelValue),
      advancedError: null,
      _orig: JSON.stringify(this.modelValue || {}),
    };
  },
  computed: {
    enabled() { return this.cfg && Object.keys(this.cfg).length > 0; },
    dirty() { return JSON.stringify(this.normalized()) !== this._orig; },
    assign() { return this.cfg.assignment_config || {}; },
  },
  watch: {
    modelValue(v) {
      if (JSON.stringify(v || {}) === JSON.stringify(this.normalized())) return;
      this.cfg = this.clone(v);
      this.sourceWorkflowId = (v || {}).opportunity_workflow_id || null;
      this.primaryKeys = [...((v || {}).opportunity_primary_keys || [])];
      this.priorityBase = ((v || {}).priority || {}).base ?? 0;
      this.priorityFlags = this.derivePriorityFlags(v);
      this.useFlagPriority = ((v || {}).priority || {}).use_flag_priority === true;
      this.useDueDatePriority = this.deriveUseDueDate(v);
      this.dueWeight = this.deriveDue(v, 'weight', null);
      this.dueMaxPoints = this.deriveDue(v, 'max_points', null);
      this.dueHorizon = this.deriveDue(v, 'horizon', null);
      this.updateMode = this.deriveUpdateMode(v);
      this.updateFieldsText = this.deriveUpdateFields(v);
      this.dossiers = [...((v || {}).on_create_dossiers || [])];
      this.supersededBy = [...((v || {}).superseded_by || [])];
      this.supersedesList = [...((v || {}).supersedes || [])];
      this.advancedJson = this.deriveAdvanced(v);
      this._orig = JSON.stringify(v || {});
    },
  },
  methods: {
    clone(o) { return JSON.parse(JSON.stringify(o || {})); },
    deriveUpdateMode(v) {
      const us = (v || {}).update_strategy;
      if (us === undefined || us === 'none') return 'none';
      if (us === 'merge') return 'merge';
      if (us && us.metadata_fields === 'all') return 'all';
      if (us && Array.isArray(us.metadata_fields)) return 'fields';
      return 'all';
    },
    deriveUpdateFields(v) {
      const us = (v || {}).update_strategy;
      return (us && Array.isArray(us.metadata_fields)) ? us.metadata_fields.join(', ') : '';
    },
    deriveAdvanced(v) {
      const adv = {};
      for (const k of ADVANCED_KEYS) if (v && v[k] !== undefined) adv[k] = v[k];
      return Object.keys(adv).length ? JSON.stringify(adv, null, 2) : '';
    },
    derivePriorityFlags(v) {
      const flags = ((v || {}).priority || {}).flags;
      if (!Array.isArray(flags)) return [];
      return flags.map(f => ({
        flag_id: f.flag_id ?? f.flag_metric_id ?? null,
        weight: f.weight ?? 0,
      }));
    },

    toggleEnabled(on) {
      if (on) {
        this.cfg = {
          create_strategy: 'create', update_strategy: { metadata_fields: 'all' },
          expire_strategy: 'expire_missing', unexpire_strategy: 'reactivate',
          opportunity_primary_keys: ['PATIENT_IDENTIFIER'], assignment_config: { auto_assign: false },
        };
        this.primaryKeys = ['PATIENT_IDENTIFIER'];
        this.priorityBase = 0; this.priorityFlags = []; this.dossiers = [];
        this.supersededBy = []; this.supersedesList = [];
        this.updateMode = 'all'; this.updateFieldsText = '';
      } else {
        this.cfg = {}; this.primaryKeys = []; this.priorityBase = 0; this.priorityFlags = [];
        this.dossiers = []; this.supersededBy = []; this.supersedesList = [];
        this.advancedJson = '';
      }
      this.emit();
    },
    onUpdateMode() {
      if (this.updateMode === 'all') this.cfg.update_strategy = { metadata_fields: 'all' };
      else if (this.updateMode === 'none') this.cfg.update_strategy = 'none';
      else if (this.updateMode === 'merge') this.cfg.update_strategy = 'merge';
      else this.cfg.update_strategy = { metadata_fields: this.parseFields(this.updateFieldsText) };
      this.emit();
    },
    onUpdateFields() {
      this.cfg.update_strategy = { metadata_fields: this.parseFields(this.updateFieldsText) };
      this.emit();
    },
    parseFields(t) { return (t || '').split(',').map(s => s.trim()).filter(Boolean); },
    onSourceWorkflow() {
      if (this.sourceWorkflowId) this.cfg.opportunity_workflow_id = this.sourceWorkflowId;
      else delete this.cfg.opportunity_workflow_id;
      this.emit();
    },
    addKey() { this.primaryKeys.push(''); this.onPrimaryKeys(); },
    removeKey(i) { this.primaryKeys.splice(i, 1); this.onPrimaryKeys(); },
    onPrimaryKeys() {
      this.cfg.opportunity_primary_keys = this.primaryKeys.map(k => k.trim()).filter(Boolean);
      this.emit();
    },
    isCategorical(flagId) {
      const fl = this.patientFlags.find(f => f.id === flagId);
      return !!fl && fl.type === 'categorical';
    },
    // A categorical flag's values are RAW SCORES, many of which share a tier —
    // LACE maps 1..16 (+999) onto Low/Med/High/No-Admit, ECHO 0..8 onto three.
    // Collapse to one entry per distinct (label, weight) and describe the raw
    // scores as a range, so the UI shows 4 chips rather than 17 near-duplicates.
    // Grouping on label+weight, not label alone: two tiers sharing a name but
    // scoring differently are genuinely different and must stay separate.
    categoricalTiers(flagId) {
      const fl = this.patientFlags.find(f => f.id === flagId);
      if (!fl || !Array.isArray(fl.values)) return [];
      const groups = new Map();
      fl.values.forEach((v) => {
        const weight = Number(v.weight) || 0;
        const label = v.label || String(v.value);
        const key = `${label}:${weight}`;
        if (!groups.has(key)) {
          groups.set(key, { label, weight, color: v.color, values: [] });
        }
        const n = Number(v.value);
        groups.get(key).values.push(Number.isNaN(n) ? v.value : n);
      });
      const tiers = [...groups.values()].map((g) => {
        const nums = g.values.filter((n) => typeof n === 'number').sort((a, b) => a - b);
        return { ...g, range: this.rangeLabel(nums.length ? nums : g.values) };
      });
      // Heaviest first — the tier that moves priority most is what you check.
      return tiers.sort((a, b) => b.weight - a.weight);
    },
    // "1–7" for a contiguous run, "5" for one, "1–3, 8" when it is not contiguous
    // (a gap is worth seeing: it usually means a value was missed, which is how
    // ECHO 4 sat unlabelled as High for two weeks).
    rangeLabel(values) {
      if (!values.length) return '';
      if (values.some((v) => typeof v !== 'number')) return values.join(', ');
      const parts = [];
      let start = values[0];
      let prev = values[0];
      for (let i = 1; i <= values.length; i += 1) {
        const cur = values[i];
        if (cur !== prev + 1) {
          parts.push(start === prev ? `${start}` : `${start}\u2013${prev}`);
          start = cur;
        }
        prev = cur;
      }
      return parts.join(', ');
    },
    tierChipClass(color) {
      return {
        red: 'bg-red-50 text-red-700 border-red-200',
        amber: 'bg-amber-50 text-amber-700 border-amber-200',
        rose: 'bg-rose-50 text-rose-700 border-rose-200',
        sky: 'bg-sky-50 text-sky-700 border-sky-200',
        emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        violet: 'bg-violet-50 text-violet-700 border-violet-200',
        gray: 'bg-gray-50 text-gray-600 border-gray-200',
      }[color] || 'bg-gray-50 text-gray-600 border-gray-200';
    },
    // ── due-date priority hydration ───────────────────────────────────────
    deriveUseDueDate(v) {
      const dd = ((v || {}).priority || {}).due_date;
      return !!(dd && dd.weight > 0);
    },
    deriveDue(v, key, dflt) {
      const dd = ((v || {}).priority || {}).due_date;
      return dd && dd[key] !== undefined ? dd[key] : dflt;
    },
    addPriorityFlag() { this.priorityFlags.push({ flag_id: null, weight: 0 }); this.onPriority(); },
    removePriorityFlag(i) { this.priorityFlags.splice(i, 1); this.onPriority(); },
    onPriority() { this.writePriority(); },
    onDueDate() { this.writePriority(); },
    writePriority() {
      // The `priority` block holds two INDEPENDENT terms — flag-weighted
      // (use_flag_priority + base + flags) and due-date urgency (due_date). Each
      // is written only when its own explicit toggle is on; both compose into
      // one block. When neither is on, no priority block exists at all, so a
      // legacy metric is never touched.
      const priority = {};

      if (this.useFlagPriority) {
        const flags = this.priorityFlags
          .filter(f => f.flag_id)
          .map(f => (
            // Categorical flags own their per-value weights in flag_config; don't
            // write a per-metric weight (it would be ignored by the backend).
            this.isCategorical(f.flag_id)
              ? { flag_id: f.flag_id }
              : { flag_id: f.flag_id, weight: Number(f.weight) || 0 }
          ));
        priority.use_flag_priority = true;
        priority.base = Number(this.priorityBase) || 0;
        priority.flags = flags;
      }

      if (this.useDueDatePriority) {
        const dd = { weight: Number(this.dueWeight) || 0 };
        if (this.dueMaxPoints !== null && this.dueMaxPoints !== '') dd.max_points = Number(this.dueMaxPoints);
        if (this.dueHorizon !== null && this.dueHorizon !== '') dd.horizon = Number(this.dueHorizon);
        priority.due_date = dd;
      }

      if (Object.keys(priority).length) this.cfg.priority = priority;
      else delete this.cfg.priority;
      this.emit();
    },

    setAssign(key, val) {
      const a = { ...(this.cfg.assignment_config || {}) };
      if (val) a[key] = true; else delete a[key];
      this.cfg.assignment_config = a;
      this.emit();
    },
    setExclude(val) {
      if (val) this.cfg.exclude_from_general_sync = true;
      else delete this.cfg.exclude_from_general_sync;
      this.emit();
    },
    onDossiers(list) {
      // Absent rather than [] when there are none, so a metric that never had
      // dossiers is not gratuitously marked dirty by opening this panel.
      if (list && list.length) this.cfg.on_create_dossiers = list;
      else delete this.cfg.on_create_dossiers;
      this.emit();
    },
    // Empty means DELETE the key — except where the metric already stored an
    // empty array. The backend treats [] and absent identically (both falsy at
    // the two read sites), so rewriting one to the other changes nothing but
    // WOULD mark the panel dirty the instant it opens. One tenant-8 metric
    // stores [] today; a spurious "Unsaved" is exactly the confusion that cost
    // two days on the flag-save ticket.
    onSupersededBy(list) {
      this.supersededBy = list;
      this._writeList('superseded_by', list);
      this.emit();
    },
    onSupersedes(list) {
      this.supersedesList = list;
      this._writeList('supersedes', list);
      this.emit();
    },
    _writeList(key, list) {
      if (list && list.length) this.cfg[key] = list;
      else if (Array.isArray((this.modelValue || {})[key])
               && !(this.modelValue || {})[key].length) this.cfg[key] = [];
      else delete this.cfg[key];
    },
    onAdvanced() {
      if (!this.advancedJson.trim()) { this.advancedError = null; this.emit(); return; }
      try { JSON.parse(this.advancedJson); this.advancedError = null; this.emit(); }
      catch (e) { this.advancedError = e.message; }
    },
    normalized() {
      // Build the final opportunity_config from structured fields + advanced JSON.
      //
      // START FROM THE EXISTING CONFIG, NOT {}. This panel only knows
      // STRUCTURED_KEYS + ADVANCED_KEYS; opportunity_config legitimately carries
      // other keys written by migrations/seed commands (surescripts_lifecycle,
      // on_create_dossiers, excluded_contract_ids, ...). Rebuilding from an empty
      // object silently DROPPED those on every save — an unrelated metric edit
      // wiped the Surescripts flag on 2026-07-22/23 and froze the enrollment
      // panel. Cloning first makes an unknown key pass through untouched.
      if (!this.enabled) return {};
      const out = this.clone(this.cfg);
      // ADVANCED_KEYS are governed ENTIRELY by the textarea, so clear them here
      // and re-apply below — otherwise deleting one from the JSON wouldn't remove
      // it (the clone would keep the old value).
      for (const k of ADVANCED_KEYS) delete out[k];
      for (const k of STRUCTURED_KEYS) {
        if (k === 'opportunity_primary_keys') continue;
        if (this.cfg[k] !== undefined) out[k] = this.cfg[k];
      }
      const pk = this.primaryKeys.map(k => k.trim()).filter(Boolean);
      if (pk.length) out.opportunity_primary_keys = pk;
      else delete out.opportunity_primary_keys;
      if (this.advancedJson.trim() && !this.advancedError) {
        try { Object.assign(out, JSON.parse(this.advancedJson)); } catch (e) { /* keep structured */ }
      }
      return out;
    },
    emit() { this.$emit('update:modelValue', this.normalized()); },
    save() { if (!this.advancedError) this.$emit('save'); },
    revert() { this.$emit('revert'); },
  },
};
</script>
